import type { ChangeEvent, KeyboardEvent } from 'react';
import React, { useEffect, useState } from 'react';

import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  XIcon,
} from 'lucide-react';
import type { Range } from 'slate';
import { AlineInput } from 'src/AlineInput';
import { AlineButton, AlineCheckbox, AlineTooltip } from 'src/components';
import { InfoIcon } from 'src/components/icons/InfoIcon';
import { SearchIcon } from 'src/components/icons/SearchIcon';
import {
  useEditorFindStateActions,
  useEditorFindStateSelector,
} from 'src/editor/editor-find-state-registry';
import { domElIdCreators } from 'src/helpers';
import { useFocused } from 'src/layout-engine/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    marginTop: 40,
  },
  advancedModeContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  checkboxLabel: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: 10,
  },
  checkboxRoot: {
    padding: 0,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  headerText: {
    color: 'inherit',
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '0.15px',
    lineHeight: 1.75,
  },
  inputLabel: {
    display: 'block',
    marginBottom: 10,
  },
  inputLabelText: {
    display: 'block',
    marginBottom: 4,
  },
  inputRoot: {
    marginLeft: theme.spacing(1),
  },
  labelText: {
    fontSize: 14,
    letterSpacing: '0.15px',
    lineHeight: 1.75,
  },
  matchAccentsContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
    marginTop: 10,
  },
  matchAccentsInfoIcon: {
    color: theme.appColors.iconsType,
    height: 16,
    width: 16,
  },
  navigationButtons: {
    display: 'flex',
    gap: 10,
    marginLeft: 'auto',
  },
  replaceButtons: {
    display: 'flex',
    gap: 10,
  },
  replaceInput: {
    marginBottom: 10,
  },
  root: {
    background: 'white',
    border: theme.appBorders.outline,
    borderRadius: 8,
    boxShadow: '0px 4px 9.1px 0px rgba(0, 0, 0, 0.25)',
    color: theme.appColors.iconsType,
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    position: 'absolute',
    right: 20,
    top: 20,
    width: 450,
    zIndex: theme.zIndex.modal,
  },
  separator: {
    backgroundColor: '#C8C8C8',
    height: 26,
    width: 1,
  },
}));

export const activeSearchHitVar = makeVar<Range | null>(null);
export function useActiveSearchHit(): Range | null {
  return useReactiveVar(activeSearchHitVar);
}

export function getIsInputElement(element: HTMLElement | null): element is HTMLInputElement {
  return element?.tagName.toLowerCase() === 'input';
}

interface BaseFindPopperProps {
  navigateToNextResult: () => void;
  navigateToPreviousResult: () => void;
  onClose: () => void;
  canMatchAccents?: boolean;
}

interface FindAndReplacePopperProps extends BaseFindPopperProps {
  canReplace: true;
  onReplaceAll: () => void;
  onReplaceCurrent: () => void;
}

interface FindOnlyPopperProps extends BaseFindPopperProps {
  canReplace?: false;
}

type FindPopperProps = FindAndReplacePopperProps | FindOnlyPopperProps;

export function FindPopper({
  canMatchAccents = false,
  navigateToNextResult,
  navigateToPreviousResult,
  onClose,
  ...props
}: FindPopperProps): JSX.Element | null {
  const { classes } = useStyles();
  const findActions = useEditorFindStateActions();
  const isEditorFocused = useFocused();
  const matchAccents = useEditorFindStateSelector(state => state.matchAccents);
  const matchCase = useEditorFindStateSelector(state => state.matchCase);
  const replace = useEditorFindStateSelector(state => state.replace);
  const search = useEditorFindStateSelector(state => state.search);
  const [isAdvancedModeOpen, setIsAdvancedModeOpen] = useState(false);

  useEffect(() => {
    if (isEditorFocused) activeSearchHitVar(null);
  }, [isEditorFocused]);

  function handleSetSearch(event: ChangeEvent<HTMLInputElement>) {
    findActions.setSearch(event.target.value);
  }

  function handleSetReplace(event: ChangeEvent<HTMLInputElement>) {
    findActions.setReplace(event.target.value);
  }

  function handleSetMatchCase(event: ChangeEvent<HTMLInputElement>) {
    findActions.setMatchCase(event.target.checked);
  }

  function handleSetMatchAccents(event: ChangeEvent<HTMLInputElement>) {
    findActions.setMatchAccents(event.target.checked);
  }

  function closeSearch() {
    if (isAdvancedModeOpen) {
      setIsAdvancedModeOpen(false);
      findActions.clearAdvancedSearchOptions();
      return;
    }
    findActions.clearSearch();
    activeSearchHitVar(null);
    onClose();
  }

  function handleFindInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'Enter':
        return event.shiftKey ? navigateToPreviousResult() : navigateToNextResult();
      case 'Escape':
        return closeSearch();
    }
  }

  if (typeof search !== 'string') return null;

  if (!isAdvancedModeOpen) {
    return (
      <div className={classes.root} role="dialog">
        <div className={classes.advancedModeContainer}>
          <AlineInput
            autoFocus
            classes={{ root: classes.inputRoot }}
            id={domElIdCreators.alineSearchInputEl()}
            placeholder="Find in document"
            startAdornment={<SearchIcon />}
            value={search}
            onChange={handleSetSearch}
            onKeyDown={handleFindInputKeyDown}
          />

          <AlineTooltip arrow placement="bottom" title="Next match">
            <IconButton
              aria-label="Next match"
              color="primary"
              size="small"
              onClick={navigateToNextResult}
            >
              <ChevronDownIcon />
            </IconButton>
          </AlineTooltip>

          <AlineTooltip arrow placement="bottom" title="Previous match">
            <IconButton
              aria-label="Previous match"
              color="primary"
              size="small"
              onClick={navigateToPreviousResult}
            >
              <ChevronUpIcon />
            </IconButton>
          </AlineTooltip>

          <div className={classes.separator}></div>

          <AlineTooltip arrow placement="bottom" title="Open advanced mode">
            <IconButton
              aria-label="Open advanced mode"
              color="primary"
              size="small"
              onClick={() => setIsAdvancedModeOpen(true)}
            >
              <EllipsisVerticalIcon />
            </IconButton>
          </AlineTooltip>

          <IconButton aria-label="Close" size="small" onClick={closeSearch}>
            <XIcon />
          </IconButton>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root} role="dialog">
      <div className={classes.header}>
        <Typography className={classes.headerText}>
          {props.canReplace ? 'Find and replace' : 'Find in document'}
        </Typography>

        <IconButton aria-label="Close" size="small" onClick={closeSearch}>
          <XIcon />
        </IconButton>
      </div>

      <label className={classes.inputLabel}>
        {props.canReplace && (
          <span className={clsx(classes.inputLabelText, classes.labelText)}>Find</span>
        )}
        <AlineInput
          autoFocus
          classes={{ root: classes.inputRoot }}
          id={domElIdCreators.alineSearchInputEl()}
          value={search}
          onChange={handleSetSearch}
          onKeyDown={handleFindInputKeyDown}
        />
      </label>

      {props.canReplace && (
        <label className={classes.inputLabel}>
          <span className={clsx(classes.inputLabelText, classes.labelText)}>Replace with</span>
          <AlineInput
            classes={{ root: classes.inputRoot }}
            value={replace}
            onChange={handleSetReplace}
            onKeyDown={event => {
              if (event.key === 'Escape') closeSearch();
            }}
          />
        </label>
      )}

      <label className={classes.checkboxLabel}>
        <AlineCheckbox
          checked={matchCase}
          classes={{ root: classes.checkboxRoot }}
          onChange={handleSetMatchCase}
        />
        <span className={classes.labelText}>Match case</span>
      </label>

      {canMatchAccents && (
        <div className={classes.matchAccentsContainer}>
          <label className={classes.checkboxLabel}>
            <AlineCheckbox
              checked={matchAccents}
              classes={{ root: classes.checkboxRoot }}
              onChange={handleSetMatchAccents}
            />
            <span className={classes.labelText}>Match accents</span>
          </label>
          <AlineTooltip
            arrow
            placement="top"
            title='Treat accented letters and their base letters as distinct. For example, searching for "resume" will not match "résumé".'
          >
            <InfoIcon className={classes.matchAccentsInfoIcon} fill="currentColor" />
          </AlineTooltip>
        </div>
      )}

      <div className={classes.actions}>
        {props.canReplace && (
          <div className={classes.replaceButtons}>
            <AlineButton
              color="primary"
              size="medium"
              variant="outlined"
              onClick={props.onReplaceAll}
            >
              Replace All
            </AlineButton>
            <AlineButton
              color="primary"
              size="medium"
              variant="outlined"
              onClick={props.onReplaceCurrent}
            >
              Replace
            </AlineButton>
          </div>
        )}

        <div className={classes.navigationButtons}>
          <AlineTooltip arrow placement="top" title="Previous match">
            <IconButton
              aria-label="Previous match"
              color="primary"
              size="small"
              onClick={navigateToPreviousResult}
            >
              <ChevronLeftIcon />
            </IconButton>
          </AlineTooltip>
          <AlineTooltip arrow placement="top" title="Next match">
            <IconButton
              aria-label="Next match"
              color="primary"
              size="small"
              onClick={navigateToNextResult}
            >
              <ChevronRightIcon />
            </IconButton>
          </AlineTooltip>
        </div>
      </div>
    </div>
  );
}
